import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getUnreadMessagesCount } from '../services/messages';
import { authApi } from '../services/auth';

// 确保 React 已加载的防护检查
if (typeof React === 'undefined') {
  throw new Error('React is not loaded. Please check if react-vendor chunk is loaded first.');
}
if (React && typeof React.createContext !== 'function') {
  throw new Error('React.createContext is not available. Please check if react-vendor chunk is loaded first.');
}

// 用户信息接口
export interface User {
  id: string;
  name: string;
  username: string;
  email: string;
  avatar: string;
  gender?: string; // 新增性别字段
  level?: string;
  unreadMessages?: number;
  phone?: string;
  bio?: string;
  birthday?: string;
  location?: string;
  province?: string;
  city?: string;
  address?: string;
  nickname?: string;
  joinDate?: string;
  points?: number;
  orders?: number;
  _lastUpdated?: number; // 内部使用：最后更新时间戳，用于触发UI刷新
  
  // 审核相关字段
  profile_audit_status?: string; // 个人信息审核状态
  has_pending_profile?: boolean; // 是否有待审核的个人信息
  pending_profile?: {
    avatar?: string;
    nickname?: string;
    bio?: string;
    address?: string;
    fields?: string[];
  };
}

// 认证上下文接口
interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  loading: boolean;
  login: (userData: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  refreshUser: () => Promise<void>;
  refreshUnreadMessages: () => Promise<void>;
}

// 创建认证上下文
// 使用函数延迟创建，确保 React 已加载
const createAuthContext = () => {
  if (typeof createContext === 'undefined') {
    throw new Error('createContext is not available. React may not be loaded yet.');
  }
  return createContext<AuthContextType | undefined>(undefined);
};

const AuthContext = createAuthContext();

// 认证提供器组件
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false); // 标记是否正在登录

  // 检查本地存储中的登录状态，并处理SSO登录（URL中的access_token）
  useEffect(() => {
    console.log('🔍 [AuthContext] 开始检查认证状态...');
    
    // 添加超时保护，确保即使出现问题也不会一直加载
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ [AuthContext] 认证检查超时，强制设置 loading=false');
      setLoading(false);
    }, 5000); // 5秒超时
    
    const checkAuthStatus = async () => {
      try {
        // 首先检查URL中是否有access_token（SSO登录）
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('access_token');
        
        if (accessToken) {
          console.log('🔐 [AuthContext] 检测到SSO登录token，开始处理...');
          
          // 保存token到localStorage
          localStorage.setItem('userToken', accessToken);
          
          // 调用API获取用户信息，添加超时保护和重试机制
          let retryCount = 0;
          const maxRetries = 3;
          let userData: User | null = null;
          
          while (retryCount < maxRetries && !userData) {
            try {
              const profilePromise = authApi.getProfile();
              const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('获取用户信息超时')), 10000)
              );
              
              userData = await Promise.race([profilePromise, timeoutPromise]) as User;
              setUser(userData);
              localStorage.setItem('userData', JSON.stringify(userData));
              
              // 清除URL中的access_token参数，避免泄露
              urlParams.delete('access_token');
              const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
              window.history.replaceState({}, '', newUrl);
              
              console.log('✅ [AuthContext] SSO登录成功，用户信息已加载');
              break; // 成功获取用户信息，退出重试循环
            } catch (error: any) {
              retryCount++;
              const errorMsg = error?.message || String(error);
              const isTimeout = errorMsg.includes('超时') || errorMsg.includes('timeout');
              const is401 = error?.response?.status === 401 || error?.status === 401;
              
              console.warn(`⚠️ [AuthContext] 获取用户信息失败 (尝试 ${retryCount}/${maxRetries}):`, errorMsg);
              
              // 如果是401错误，可能是token还未生效，等待后重试
              if (is401 && retryCount < maxRetries) {
                console.log(`⏳ [AuthContext] Token可能还未生效，等待 ${retryCount * 500}ms 后重试...`);
                await new Promise(resolve => setTimeout(resolve, retryCount * 500));
                continue; // 重试
              }
              
              // 如果是超时且还有重试机会，继续重试
              if (isTimeout && retryCount < maxRetries) {
                console.log(`⏳ [AuthContext] 请求超时，等待 ${retryCount * 500}ms 后重试...`);
                await new Promise(resolve => setTimeout(resolve, retryCount * 500));
                continue; // 重试
              }
              
              // 如果所有重试都失败，检查是否是401错误
              if (retryCount >= maxRetries) {
                if (is401) {
                  console.error('❌ [AuthContext] SSO登录失败：Token无效或已过期');
                  localStorage.removeItem('userToken');
                } else {
                  // 非401错误，可能是网络问题，保留token让用户稍后重试
                  console.error('❌ [AuthContext] SSO登录失败：获取用户信息时发生错误，但保留token以便稍后重试');
                  console.error('错误详情:', error);
                }
              }
            }
          }
          
          // 如果最终还是没有获取到用户信息，尝试从localStorage恢复
          if (!userData) {
            console.error('❌ [AuthContext] SSO登录失败：无法获取用户信息');
            // 尝试从localStorage恢复用户信息（可能是之前保存的）
            const savedUserStr = localStorage.getItem('userData');
            if (savedUserStr) {
              try {
                const savedUser = JSON.parse(savedUserStr);
                console.log('✅ [AuthContext] 从localStorage恢复用户信息');
                setUser(savedUser);
                userData = savedUser;
              } catch (e) {
                console.error('❌ [AuthContext] 解析保存的用户信息失败:', e);
              }
            }
            
            // 如果还是没有用户信息，但token存在，可能是网络问题
            // 不清除token，让用户稍后重试或刷新页面
            if (!userData) {
              console.warn('⚠️ [AuthContext] Token存在但无法获取用户信息，可能是网络问题。用户可能需要刷新页面。');
            }
          }
          
          clearTimeout(timeoutId);
          setLoading(false);
          return;
        }
        
        // 常规登录检查：从localStorage读取
        console.log('🔍 [AuthContext] 检查本地存储中的登录信息...');
        const token = localStorage.getItem('userToken');
        const userStr = localStorage.getItem('userData');
        
        if (token && userStr) {
          try {
            const userData = JSON.parse(userStr);
            setUser(userData);
            console.log('✅ [AuthContext] 从本地存储恢复用户信息');
          } catch (parseError) {
            console.error('❌ [AuthContext] 解析用户数据失败:', parseError);
            localStorage.removeItem('userToken');
            localStorage.removeItem('userData');
          }
        } else {
          console.log('ℹ️ [AuthContext] 未找到本地登录信息');
        }
      } catch (error) {
        console.error('❌ [AuthContext] Parse user data error:', error);
        // 清除无效数据
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
        console.log('✅ [AuthContext] 认证检查完成，loading=false');
      }
    };

    checkAuthStatus();
  }, []);

  // 监听全局401未授权事件
  useEffect(() => {
    const handleUnauthorized = () => {
      // 🔒 保护机制：如果正在登录过程中，忽略401错误
      // 因为登录后立即调用的API可能会返回401（token还未完全生效）
      if (isLoggingIn) {
        console.warn('⚠️ 检测到401错误，但正在登录中，忽略此错误');
        return;
      }
      
      console.warn('🚨 检测到401未授权事件，用户登录已过期');
      
      // 获取当前路径，用于登录后重定向
      const currentPath = window.location.pathname + window.location.search;
      
      // 只在非登录页面时保存重定向路径
      if (!currentPath.includes('/auth') && !currentPath.includes('/login')) {
        sessionStorage.setItem('redirectPath', currentPath);
      }
      
      // 自动登出（清除 React 状态）
      logout();
      
      // 注意：localStorage 已经在 api.ts 的 handleTokenExpired() 中清除了
      // 这里只需要更新 React 状态，让 Header 重新渲染显示登录按钮
      // 不需要刷新页面或跳转，让用户自然地看到登录按钮出现
    };

    window.addEventListener('api:unauthorized', handleUnauthorized);
    
    return () => {
      window.removeEventListener('api:unauthorized', handleUnauthorized);
    };
  }, [isLoggingIn]); // 依赖 isLoggingIn 状态

  // 页面加载时刷新未读消息数量
  useEffect(() => {
    // 必须同时满足：用户对象存在 且 token存在
    const token = localStorage.getItem('userToken');
    
    // 如果用户未登录或token不存在，直接返回，不执行任何操作
    if (!user || !token || !user.id) {
      console.log('用户未登录或token不存在，跳过刷新未读消息');
      return;
    }

    // 验证 token 格式是否有效（简单检查是否为空字符串或无效值）
    if (token.trim().length === 0) {
      console.log('Token格式无效，跳过刷新未读消息');
      return;
    }

    // 页面加载时刷新一次（延迟500ms，确保登录流程完全完成）
    const refreshUnreadMessagesInternal = async () => {
      // 在异步函数内部再次检查，防止在异步执行期间用户登出
      const currentToken = localStorage.getItem('userToken');
      if (!currentToken || currentToken.trim().length === 0) {
        console.log('Token已失效，取消刷新未读消息');
        return;
      }

      try {
        const response = await getUnreadMessagesCount();
        updateUser({ unreadMessages: response.unread_count });
      } catch (error) {
        // 静默处理错误，避免影响用户体验
        console.warn('刷新未读消息数量失败:', error);
        
        // ⚠️ 注意：不要在这里自动登出！
        // 因为 api.ts 中的 handleTokenExpired() 已经处理了 401 错误
        // 如果在这里再次调用 logout()，会导致重复清理
        // 只需要设置未读消息数为 0 即可
        updateUser({ unreadMessages: 0 });
      }
    };

    // 延迟500ms执行，确保登录流程完全完成，token已经生效
    const timer = setTimeout(() => {
      refreshUnreadMessagesInternal();
    }, 500);

    return () => clearTimeout(timer);
  }, [user?.id]); // 依赖用户ID，确保用户登录后刷新

  // 登录函数
  const login = (userData: User, token: string) => {
    console.log('🔐 [AuthContext] 开始登录流程');
    
    // 设置登录中标志，保护登录过程不被401错误中断
    setIsLoggingIn(true);
    
    setUser(userData);
    localStorage.setItem('userToken', token);
    localStorage.setItem('userData', JSON.stringify(userData));
    
    console.log('✅ [AuthContext] Token 已保存到 localStorage');
    
    // 1秒后清除登录中标志（给足够时间让token生效）
    setTimeout(() => {
      setIsLoggingIn(false);
      console.log('🔓 [AuthContext] 登录保护期结束');
    }, 1000);
  };

  // 登出函数
  const logout = () => {
    setUser(null);
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    
    // 清除所有作品和图片的解锁状态
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('gallery_unlocked_') || key.startsWith('unlocked_images_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log('已清除所有作品和图片的解锁状态');
  };

  // 更新用户信息
  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
  };

  // 从服务器重新获取用户信息
  const refreshUser = async () => {
    // 必须同时满足：用户对象存在 且 token存在
    const token = localStorage.getItem('userToken');
    if (!user || !token) {
      console.log('用户未登录或token不存在，跳过刷新用户信息');
      return;
    }
    
    try {
      const userData = await authApi.getProfile();
      // 创建新对象确保React检测到变化
      const updatedUser = { 
        ...user, 
        ...userData,
        // 添加时间戳确保对象引用改变
        _lastUpdated: Date.now()
      };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      console.log('用户信息已刷新:', updatedUser);
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      // 检查是否是认证失败，如果是则自动登出
      if (error instanceof Error && error.message.includes('未授权访问')) {
        console.log('登录已失效，自动登出用户');
        logout();
      }
    }
  };

  // 刷新未读消息数量
  const refreshUnreadMessages = async () => {
    // 必须同时满足：用户对象存在 且 token存在
    const token = localStorage.getItem('userToken');
    if (!user || !token || !user.id || token.trim().length === 0) {
      console.log('用户未登录或token不存在，跳过刷新未读消息');
      return;
    }
    
    try {
      const response = await getUnreadMessagesCount();
      updateUser({ unreadMessages: response.unread_count });
    } catch (error) {
      // 静默处理错误，避免影响用户体验
      console.warn('刷新未读消息数量失败:', error);
      
      // ⚠️ 注意：不要在这里自动登出！
      // 因为 api.ts 中的 handleTokenExpired() 已经处理了 401 错误
      // 如果在这里再次调用 logout()，会导致重复清理
      // 只需要设置未读消息数为 0 即可
      updateUser({ unreadMessages: 0 });
    }
  };

  const value: AuthContextType = {
    user,
    isLoggedIn: !!user,
    loading,
    login,
    logout,
    updateUser,
    refreshUser,
    refreshUnreadMessages,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// 使用认证上下文的钩子
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
